import {
  PostalCodeRegex,
  ProvinceCode,
  provinces,
  StateCode,
  states,
  statesAndProvinces,
  ZipCodeRegex,
} from '@/data/states'
import z from 'zod'

export const phoneSchema = z
  .string()
  .regex(/^\d{10}$/, 'Must be a valid phone number')

/**
 * Canonical client-side password requirements. These mirror the server-side
 * ASP.NET Identity policy configured in `AddMccsAuthentication` so the three
 * password forms (setup modal, reset, profile) cannot drift apart.
 */
export const passwordRequirements: {
  label: string
  test: (password: string) => boolean
}[] = [
  {
    label: 'At least 8 characters',
    test: (password) => password.length >= 8,
  },
  {
    label: 'At least 1 uppercase letter',
    test: (password) => /[A-Z]/.test(password),
  },
  {
    label: 'At least 1 lowercase letter',
    test: (password) => /[a-z]/.test(password),
  },
  {
    label: 'At least 1 number',
    test: (password) => /\d/.test(password),
  },
  {
    label: 'At least 1 special character',
    test: (password) => /[^A-Za-z0-9]/.test(password),
  },
]

export const passwordSchema = z
  .string({ error: 'You need to provide a password.' })
  .superRefine((password, ctx) => {
    for (const requirement of passwordRequirements) {
      if (!requirement.test(password)) {
        ctx.addIssue({
          code: 'custom',
          message: requirement.label,
        })
      }
    }
  })

export const emailSchema = z
  .email('Must be a valid email address')
  .trim()
  .max(255, 'Must be 255 characters or less')

export const stateSchema = z
  .string()
  .refine(
    (val) =>
      val === '' ||
      statesAndProvinces.find(
        (sp) => sp.code == (val.toUpperCase() as StateCode | ProvinceCode)
      ),
    {
      message: 'Invalid state or province',
    }
  )

export const addressSchema = z
  .object({
    street1: z
      .string()
      .min(1, 'Required')
      .max(100, 'Must be 100 characters or less'),
    street2: z
      .string()
      .max(100, 'Must be 100 characters or less')
      .or(z.literal('')),
    city: z
      .string()
      .min(1, 'Required')
      .max(50, 'Must be 50 characters or less'),
    stateProvince: stateSchema.min(1, 'Required'),
    postalCode: z
      .string()
      .min(1, 'Required')
      .max(10, 'Must be 10 characters or less'),
  })
  // Postal code format for US states
  .superRefine((data, ctx) => {
    if (!data.stateProvince || !data.postalCode) return // state is required, so skip if not provided
    if (!states.find((s) => s.code === data.stateProvince.toUpperCase())) return // not a US state, skip

    if (!ZipCodeRegex.test(data.postalCode)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Invalid ZIP code',
        path: ['postalCode'],
      })
    }
  })
  // Postal code format for Canadian provinces
  .superRefine((data, ctx) => {
    if (!data.stateProvince || !data.postalCode) return // state is required, so skip if not provided
    if (!provinces.find((p) => p.code === data.stateProvince.toUpperCase()))
      return // not a Canadian province, skip

    if (!PostalCodeRegex.test(data.postalCode)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Invalid postal code',
        path: ['postalCode'],
      })
    }
  })
